import { Badge, Button, DotsLoader } from '@eppendorf/vnls-react-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { usePendingInvitations } from '../dashboard.api';

import { DashboardCardTitle } from './dashboard-card-title';
import { DashboardWidget } from './dashboard-widget';
import { SectionFeedback } from './section-feedback';
import { TruncatedText } from './truncated-text';

import './pending-invitations-card.scss';

interface PendingInvitationsCardProps {
  canViewProfiles: boolean;
}

function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.[0]?.toUpperCase() ?? '';
  const last = lastName?.[0]?.toUpperCase() ?? '';
  return first + last || '?';
}

export function PendingInvitationsCard({ canViewProfiles }: PendingInvitationsCardProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = usePendingInvitations();

  return (
    <DashboardWidget className="pending-invitations-card">
      <div className="dashboard-widget__header">
        <DashboardCardTitle
          icon="mail"
          tone="gray"
          title={t('dashboard.pendingInvitations.title')}
          hint={t('dashboard.pendingInvitations.hint')}
        />
        <SectionFeedback sectionId="pending-invitations" />
      </div>
      {isLoading && <DotsLoader className="m-top-l" />}
      {isError && (
        <div className="dashboard-card__feedback" role="alert">
          <span>{t('dashboard.pendingInvitations.loadError')}</span>
          <Button
            variant="secondary"
            onClick={() => {
              refetch();
            }}
          >
            {t('dashboard.pendingInvitations.retry')}
          </Button>
        </div>
      )}
      {!isLoading && !isError && (!data || data.length === 0) && (
        <div className="pending-invitations-card__empty">
          {t('dashboard.pendingInvitations.noInvitations')}
        </div>
      )}
      {!isLoading && !isError && data && data.length > 0 && (
        <div className="pending-invitations-card__list">
          {data.map((invitation) => {
            const content = (
              <>
                <div className="pending-invitations-card__avatar">
                  {getInitials(invitation.firstName, invitation.lastName)}
                </div>
                <div className="pending-invitations-card__info">
                  <TruncatedText
                    text={
                      invitation.firstName && invitation.lastName
                        ? `${invitation.firstName} ${invitation.lastName}`
                        : invitation.email
                    }
                    className="pending-invitations-card__name"
                  />
                  <TruncatedText
                    text={invitation.email}
                    className="pending-invitations-card__email"
                  />
                </div>
                <div className="pending-invitations-card__status">
                  <Badge color="orange" size="small" bordered={false}>
                    {t('dashboard.pendingInvitations.inviteSent')}
                  </Badge>
                  {invitation.inviteSentAt && (
                    <span className="pending-invitations-card__date">
                      {invitation.inviteSentAt}
                    </span>
                  )}
                </div>
              </>
            );

            return canViewProfiles ? (
              <Link
                key={invitation.id}
                className="pending-invitations-card__item pending-invitations-card__item--clickable"
                to={{
                  search: `?sidecarRoute=user/profile&id=${invitation.id}`,
                }}
              >
                {content}
              </Link>
            ) : (
              <div key={invitation.id} className="pending-invitations-card__item">
                {content}
              </div>
            );
          })}
        </div>
      )}
    </DashboardWidget>
  );
}
